import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, IconButton, Tooltip, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem,
  Pagination, InputAdornment
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PrintIcon from "@mui/icons-material/Print";
import { fetchData, putData, deleteData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import ConfirmDialog from "../../DynamicComponent/ConfirmDialog";
import { exportToCSV, filterRows } from "../../utils/tableUtils";
import { printDocument, field, row, schoolHeader } from "../../utils/printUtils";

const STEP_LABELS = { secretary: "Step 1: Secretary", coordinator: "Step 2: Coordinator", finance: "Step 3: Finance", completed: "Complete" };
const STATUS_CONFIG = {
  received:            { label: "Received",        color: "#3b82f6", bg: "#eff6ff" },
  pending_coordinator: { label: "Pending Coordinator", color: "#f59e0b", bg: "#fffbeb" },
  pending_finance:     { label: "Pending Finance",  color: "#336599", bg: "#f5f3ff" },
  approved:            { label: "Approved",         color: "#16a34a", bg: "#f0fdf4" },
  rejected:            { label: "Rejected",         color: "#dc2626", bg: "#fef2f2" },
};
const ROWS_PER_PAGE = 10;

const AdminWithdrawal = () => {
  const [rows, setRows] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [approveDialog, setApproveDialog] = useState({ open: false, row: null });
  const [approvalRole, setApprovalRole] = useState("secretary");
  const [approvalAction, setApprovalAction] = useState("approved");
  const [approvalNote, setApprovalNote] = useState("");
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("student/withdrawl-enquirie/list");
      if (res?.data) setRows(Array.isArray(res.data) ? res.data : []);
    } catch (_) { setRows([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filterRows(rows, filterText);
  const pageCount = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const pageRows = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const openApproveDialog = (row) => {
    setApproveDialog({ open: true, row });
    setApprovalRole(row.currentStep === "coordinator" ? "coordinator" : row.currentStep === "finance" ? "finance" : "secretary");
    setApprovalAction("approved");
    setApprovalNote("");
  };

  const handleApprove = async () => {
    const { row } = approveDialog;
    try {
      await putData(`student/withdrawl-enquirie/approve-step/${row.id}`, {
        role: approvalRole, action: approvalAction, note: approvalNote
      });
      setToast({ open: true, severity: "success", message: "Approval step updated successfully!" });
      setApproveDialog({ open: false, row: null });
      load();
    } catch (err) {
      setToast({ open: true, severity: "error", message: err?.response?.data?.message || "Failed to update." });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteData(`student/withdrawl-enquirie/delete/${confirmDelete.id}`);
      setToast({ open: true, severity: "success", message: "Deleted successfully!" });
      setConfirmDelete({ open: false, id: null });
      load();
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Failed to delete." });
    }
  };

  const handleTransferCert = (rec) => {
    printDocument(`Transfer Certificate — ${rec?.studentName || rec?.firstName}`, `
      ${schoolHeader('Transfer Certificate', `Date: ${new Date().toLocaleDateString()}`)}
      <div class="section">
        <p>This is to certify that <strong>${rec?.studentName || `${rec?.firstName || ''} ${rec?.lastName || ''}`}</strong>,
        bearing Registration No. <strong>${rec?.registrationNo || rec?.registrationNumber || '—'}</strong>, was a student of Cumming Tamil School
        in Grade <strong>${rec?.grade || rec?.gradeApplyingFor || '—'}</strong>.</p>
        <br/>
        ${row(
          field('Date of Withdrawal', rec?.withdrawalDate ? new Date(rec.withdrawalDate).toLocaleDateString() : new Date().toLocaleDateString()),
          field('Reason', rec?.reason || '—')
        )}
        <br/>
        <p>This certificate is issued upon request for admission to another institution.</p>
        <p style="margin-top:30px">The student's conduct and character during their time at this institution was <strong>Good</strong>.</p>
        <div style="margin-top:40px">
          <div style="text-align:right;width:200px;margin-left:auto">
            <div style="border-top:2px solid #333;margin-bottom:6px"></div>
            <p style="font-size:12px">Principal / Administrator</p>
          </div>
        </div>
      </div>
    `);
  };

  const handleExport = () => {
    const cols = [
      { label: "Student", field: "studentName" },
      { label: "Reason", field: "reason" },
      { label: "Status", field: "status" },
      { label: "Current Step", field: "currentStep" },
    ];
    exportToCSV(cols, filtered.map(r => ({
      studentName: r.studentName || r.studentId,
      reason: r.reason,
      status: r.status,
      currentStep: r.currentStep,
    })), "withdrawal_enquiries.csv");
  };

  const StatusChip = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.received;
    return (
      <Chip label={cfg.label} size="small"
        sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "11px" }} />
    );
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
      <ConfirmDialog open={confirmDelete.open} title="Delete Withdrawal"
        message="Are you sure you want to delete this withdrawal enquiry? This cannot be undone."
        severity="danger" confirmLabel="Delete" cancelLabel="Cancel"
        onConfirm={handleDelete} onCancel={() => setConfirmDelete({ open: false, id: null })} />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 8px" }}>
        Pages / Withdrawal Management
      </p>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#111827" }}>Withdrawal Enquiries</Typography>
          <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Manage multi-step withdrawal approvals</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button startIcon={<FilterListIcon />} size="small"
            onClick={() => setShowFilter(v => !v)}
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px" }}>
            Filters
          </Button>
          <Button startIcon={<FileDownloadOutlinedIcon />} size="small" onClick={handleExport}
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px" }}>
            Export
          </Button>
        </Box>
      </Box>

      {showFilter && (
        <Box sx={{ mb: 2 }}>
          <TextField size="small" fullWidth placeholder="Search by student name, reason, status..."
            value={filterText} onChange={e => setFilterText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </Box>
      )}

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#1E293B" }}>
              <TableRow>
                {["#", "Student", "Reason", "Status", "Current Step", "Secretary", "Coordinator", "Finance", "Actions"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "12px", whiteSpace: "nowrap" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.length === 0 ? (
                <TableRow><TableCell colSpan={9} sx={{ textAlign: "center", py: 6, color: "#8F95B2" }}>No withdrawal enquiries found</TableCell></TableRow>
              ) : pageRows.map((row, idx) => (
                <TableRow key={row.id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" } }}>
                  <TableCell sx={{ fontSize: "13px", color: "#6B7280" }}>{(page - 1) * ROWS_PER_PAGE + idx + 1}</TableCell>
                  <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{row.studentName || `Student #${row.studentId}`}</TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.reason}</TableCell>
                  <TableCell><StatusChip status={row.status} /></TableCell>
                  <TableCell sx={{ fontSize: "12px", color: "#6B7280" }}>{STEP_LABELS[row.currentStep] || row.currentStep}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.secretaryStatus || "pending"}
                      sx={{ backgroundColor: row.secretaryStatus === "approved" ? "#f0fdf4" : row.secretaryStatus === "rejected" ? "#fef2f2" : "#f9fafb",
                        color: row.secretaryStatus === "approved" ? "#16a34a" : row.secretaryStatus === "rejected" ? "#dc2626" : "#6B7280",
                        fontSize: "11px" }} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={row.coordinatorStatus || "pending"}
                      sx={{ backgroundColor: row.coordinatorStatus === "approved" ? "#f0fdf4" : row.coordinatorStatus === "rejected" ? "#fef2f2" : "#f9fafb",
                        color: row.coordinatorStatus === "approved" ? "#16a34a" : row.coordinatorStatus === "rejected" ? "#dc2626" : "#6B7280",
                        fontSize: "11px" }} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={row.financeStatus || "pending"}
                      sx={{ backgroundColor: row.financeStatus === "approved" ? "#f0fdf4" : row.financeStatus === "rejected" ? "#fef2f2" : "#f9fafb",
                        color: row.financeStatus === "approved" ? "#16a34a" : row.financeStatus === "rejected" ? "#dc2626" : "#6B7280",
                        fontSize: "11px" }} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {row.currentStep !== "completed" && (
                        <Tooltip title="Process Approval">
                          <IconButton size="small" onClick={() => openApproveDialog(row)}
                            sx={{ color: "#244C77", "&:hover": { backgroundColor: "#EAF1F8" } }}>
                            <CheckCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Transfer Certificate">
                        <IconButton size="small" onClick={() => handleTransferCert(row)}
                          sx={{ color: "#16a34a", "&:hover": { backgroundColor: "#f0fdf4" } }}>
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setConfirmDelete({ open: true, id: row.id })}
                          sx={{ color: "#dc2626", "&:hover": { backgroundColor: "#fef2f2" } }}>
                          <CancelOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {pageCount > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination count={pageCount} page={page} onChange={(_, v) => setPage(v)} size="small"
              sx={{ "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#244C77", color: "#fff" } }} />
          </Box>
        )}
      </Paper>

      {/* Approval Dialog */}
      <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false, row: null })} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px" }}>Process Withdrawal Approval</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>Approval Role</Typography>
              <Select fullWidth size="small" value={approvalRole} onChange={e => setApprovalRole(e.target.value)}
                sx={{ borderRadius: "8px" }}>
                <MenuItem value="secretary">Secretary (Step 1)</MenuItem>
                <MenuItem value="coordinator">Academic Coordinator (Step 2)</MenuItem>
                <MenuItem value="finance">Finance/Treasurer (Step 3)</MenuItem>
              </Select>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>Action</Typography>
              <Select fullWidth size="small" value={approvalAction} onChange={e => setApprovalAction(e.target.value)}
                sx={{ borderRadius: "8px" }}>
                <MenuItem value="approved">Approve</MenuItem>
                <MenuItem value="rejected">Reject</MenuItem>
              </Select>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>Note (optional)</Typography>
              <TextField fullWidth size="small" multiline rows={2}
                placeholder="Add a note for this decision..."
                value={approvalNote} onChange={e => setApprovalNote(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setApproveDialog({ open: false, row: null })} fullWidth
            sx={{ border: "1.5px solid #D1D5DB", borderRadius: "10px", textTransform: "none", color: "#374151" }}>
            Cancel
          </Button>
          <Button onClick={handleApprove} fullWidth variant="contained"
            sx={{ borderRadius: "10px", backgroundColor: "#2D3A5C", textTransform: "none", fontWeight: 600, boxShadow: "none" }}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminWithdrawal;
